<template>
  <div>
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-2xl font-bold text-gray-900">Produtos</h1>
      <button
        @click="showAddModal = true"
        class="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium"
      >
        + Novo Produto
      </button>
    </div>
    
    <!-- Products Table -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Produto</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Preço</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fotos</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ações</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="product in products" :key="product.id" class="hover:bg-gray-50">
            <td class="px-6 py-4">
              <div class="flex items-center">
                <img v-if="product.image_url" :src="getImageUrl(product.image_url)" class="h-10 w-10 rounded object-cover mr-3">
                <div v-else class="h-10 w-10 rounded bg-gray-200 flex items-center justify-center mr-3">
                  <PhotoIcon class="h-5 w-5 text-gray-400" />
                </div>
                <div>
                  <p class="font-medium text-gray-900">{{ product.name }}</p>
                  <p class="text-sm text-gray-500">{{ product.category_name || 'Sem categoria' }}</p>
                </div>
              </div>
            </td>
            <td class="px-6 py-4 font-medium">{{ formatCurrency(product.current_price) }}</td>
            <td class="px-6 py-4">
              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" 
                :class="product.is_available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
                {{ product.is_available ? 'Disponível' : 'Indisponível' }}
              </span>
            </td>
            <td class="px-6 py-4">
              <span class="text-sm text-gray-600">
                {{ getImageCount(product) }}/4
              </span>
            </td>
            <td class="px-6 py-4">
              <button @click="editProduct(product)" class="text-blue-600 hover:text-blue-800 mr-3 font-medium">Editar</button>
              <button @click="manageImages(product)" class="text-purple-600 hover:text-purple-800 mr-3 font-medium">Fotos</button>
              <button @click="toggleAvailability(product)" class="text-gray-600 hover:text-gray-800 mr-3">
                {{ product.is_available ? 'Desativar' : 'Ativar' }}
              </button>
              <button @click="deleteProduct(product)" class="text-red-600 hover:text-red-800 font-medium">Excluir</button>
            </td>
          </tr>
        </tbody>
      </table>
      
      <div v-if="products.length === 0" class="p-8 text-center text-gray-500">
        Nenhum produto cadastrado
      </div>
    </div>
    
    <!-- Add Product Modal -->
    <div v-if="showAddModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div class="bg-white rounded-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <h2 class="text-xl font-bold mb-4">Novo Produto</h2>
        
        <form @submit.prevent="addProduct">
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Nome</label>
              <input v-model="newProduct.name" type="text" required class="w-full border rounded-lg px-3 py-2">
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
              <textarea v-model="newProduct.description" class="w-full border rounded-lg px-3 py-2" rows="2"></textarea>
            </div>
            
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Preço (em centavos)</label>
                <input v-model.number="newProduct.price" type="number" required class="w-full border rounded-lg px-3 py-2">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
                <select v-model.number="newProduct.category_id" class="w-full border rounded-lg px-3 py-2">
                  <option value="">Selecione</option>
                  <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
                </select>
              </div>
            </div>
          </div>
          
          <div class="flex justify-end gap-3 mt-6">
            <button type="button" @click="showAddModal = false" class="px-4 py-2 text-gray-600 hover:text-gray-800">
              Cancelar
            </button>
            <button type="submit" class="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700">
              Salvar
            </button>
          </div>
        </form>
      </div>
    </div>
    
    <!-- Edit Product Modal -->
    <div v-if="showEditModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div class="bg-white rounded-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <h2 class="text-xl font-bold mb-4">Editar Produto</h2>
        
        <form @submit.prevent="updateProduct">
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Nome</label>
              <input v-model="editProductForm.name" type="text" required class="w-full border rounded-lg px-3 py-2">
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
              <textarea v-model="editProductForm.description" class="w-full border rounded-lg px-3 py-2" rows="2"></textarea>
            </div>
            
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Preço (em centavos)</label>
                <input v-model.number="editProductForm.price" type="number" required class="w-full border rounded-lg px-3 py-2">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
                <select v-model.number="editProductForm.category_id" class="w-full border rounded-lg px-3 py-2">
                  <option value="">Selecione</option>
                  <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
                </select>
              </div>
            </div>
            
            <div class="flex items-center">
              <label class="flex items-center cursor-pointer">
                <input v-model="editProductForm.is_available" type="checkbox" class="mr-2">
                <span class="text-sm">Disponível</span>
              </label>
            </div>
          </div>
          
          <div class="flex justify-end gap-3 mt-6">
            <button type="button" @click="showEditModal = false" class="px-4 py-2 text-gray-600 hover:text-gray-800">
              Cancelar
            </button>
            <button type="submit" class="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700">
              Atualizar
            </button>
          </div>
        </form>
      </div>
    </div>
    
    <!-- Manage Images Modal -->
    <div v-if="showImagesModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div class="bg-white rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <h2 class="text-xl font-bold mb-4">Gerenciar Fotos - {{ selectedProduct?.name }}</h2>
        
        <!-- Image Grid -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <!-- Main Image -->
          <div v-if="selectedProduct?.image_url" class="relative group">
            <div class="aspect-square rounded-lg overflow-hidden border-2 border-orange-500">
              <img :src="getImageUrl(selectedProduct.image_url)" class="w-full h-full object-cover">
            </div>
            <div class="absolute top-2 left-2 bg-orange-500 text-white text-xs px-2 py-1 rounded">Principal</div>
            <button 
              @click="deleteImage(selectedProduct.image_url)"
              class="absolute top-2 right-2 bg-red-500 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <TrashIcon class="h-4 w-4" />
            </button>
          </div>
          
          <!-- Additional Images -->
          <div 
            v-for="(img, index) in selectedProduct?.additional_images || []" 
            :key="index"
            class="relative group"
          >
            <div class="aspect-square rounded-lg overflow-hidden border border-gray-200">
              <img :src="getImageUrl(img)" class="w-full h-full object-cover">
            </div>
            <button 
              @click="setMainImage(img)"
              class="absolute bottom-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
            >
              Tornar Principal
            </button>
            <button 
              @click="deleteImage(img)"
              class="absolute top-2 right-2 bg-red-500 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <TrashIcon class="h-4 w-4" />
            </button>
          </div>
          
          <!-- Upload Slot -->
          <div 
            v-if="canAddMoreImages"
            class="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-orange-500 hover:bg-orange-50 transition-colors"
            @click="triggerFileInput"
          >
            <PlusIcon class="h-8 w-8 text-gray-400 mb-2" />
            <span class="text-sm text-gray-500">Adicionar Foto</span>
            <span class="text-xs text-gray-400">{{ getImageCount(selectedProduct) }}/4</span>
          </div>
        </div>
        
        <!-- Upload Progress -->
        <div v-if="uploading" class="mb-4">
          <div class="flex items-center justify-between text-sm mb-1">
            <span>Enviando...</span>
            <span>{{ uploadProgress }}%</span>
          </div>
          <div class="w-full bg-gray-200 rounded-full h-2">
            <div class="bg-orange-500 h-2 rounded-full transition-all" :style="{ width: uploadProgress + '%' }"></div>
          </div>
        </div>
        
        <!-- Hidden File Input -->
        <input 
          ref="fileInput"
          type="file" 
          accept="image/*"
          class="hidden"
          @change="handleFileUpload"
        >
        
        <div class="flex justify-end gap-3">
          <button @click="showImagesModal = false" class="px-4 py-2 text-gray-600 hover:text-gray-800">
            Fechar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { PhotoIcon, TrashIcon, PlusIcon } from '@heroicons/vue/24/outline'
import { useAuthStore } from '@/stores'
import { productApi, categoryApi } from '@/utils/api'
import { formatCurrency } from '@/utils/formatters'
import { notifyError, notifySuccess } from '@/utils/notifications'
import { confirmDialog } from '@/composables/useConfirm'
import type { Product, Category } from '@/types'

const authStore = useAuthStore()

const products = ref<Product[]>([])
const categories = ref<Category[]>([])
const showAddModal = ref(false)
const showEditModal = ref(false)
const showImagesModal = ref(false)
const selectedProduct = ref<Product | null>(null)
const editingProductId = ref<number | null>(null)
const uploading = ref(false)
const uploadProgress = ref(0)
const fileInput = ref<HTMLInputElement | null>(null)

const newProduct = ref({
  name: '',
  description: '',
  price: 0,
  category_id: null as number | null
})

const editProductForm = ref({
  name: '',
  description: '',
  price: 0,
  category_id: null as number | null,
  is_available: true
})

const canAddMoreImages = computed(() => {
  if (!selectedProduct.value) return false
  const count = getImageCount(selectedProduct.value)
  return count < 4
})

const getImageUrl = (url: string) => {
  if (url.startsWith('http')) return url
  return url
}

const getImageCount = (product: Product | null) => {
  if (!product) return 0
  let count = product.image_url ? 1 : 0
  if (product.additional_images) {
    count += product.additional_images.length
  }
  return count
}

const loadProducts = async () => {
  const companyId = authStore.companyId || 1
  try {
    const res = await productApi.getByCompanyAdmin(companyId)
    products.value = res.data
  } catch (error) {
    console.error('Erro ao carregar produtos:', error)
  }
}

const loadCategories = async () => {
  const companyId = authStore.companyId || 1
  try {
    const res = await categoryApi.getByCompanyAdmin(companyId)
    categories.value = res.data
  } catch (error) {
    console.error('Erro ao carregar categorias:', error)
  }
}

const toggleAvailability = async (product: Product) => {
  try {
    await productApi.toggleAvailability(product.id)
    product.is_available = !product.is_available
  } catch (error) {
    notifyError('Erro ao atualizar status')
  }
}

const editProduct = (product: Product) => {
  editingProductId.value = product.id
  editProductForm.value = {
    name: product.name,
    description: product.description || '',
    price: product.price,
    category_id: product.category_id,
    is_available: product.is_available
  }
  showEditModal.value = true
}

const formatApiError = (error: any): string => {
  const detail = error.response?.data?.detail
  if (Array.isArray(detail)) {
    return detail.map((err: any) => {
      if (typeof err === 'string') return err
      const loc = err.loc?.slice(1).join('.') || ''
      const msg = err.msg || 'Erro de validação'
      if (loc) return `${loc}: ${msg}`
      return msg
    }).join('\n')
  }
  if (typeof detail === 'object' && detail !== null) {
    return detail.msg || JSON.stringify(detail)
  }
  return detail || error.message || 'Erro ao processar requisição'
}

const updateProduct = async () => {
  if (!editingProductId.value) return

  if (!editProductForm.value.category_id) {
    notifyError('Deve escolher a categoria')
    return
  }

  try {
    await productApi.update(editingProductId.value, editProductForm.value)
    showEditModal.value = false
    editingProductId.value = null
    loadProducts()
    notifySuccess('Produto atualizado com sucesso!')
  } catch (error: any) {
    notifyError(formatApiError(error))
  }
}

const deleteProduct = async (product: Product) => {
  if (!await confirmDialog({ title: 'Confirmar exclusão', message: `Tem certeza que deseja excluir o produto "${product.name}"?`, confirmLabel: 'Excluir', danger: true })) return
  
  try {
    await productApi.delete(product.id)
    loadProducts()
    notifySuccess('Produto excluído com sucesso!')
  } catch (error: any) {
    notifyError(error.response?.data?.detail || 'Erro ao excluir produto')
  }
}

const manageImages = (product: Product) => {
  selectedProduct.value = product
  showImagesModal.value = true
}

const addProduct = async () => {
  const companyId = authStore.companyId || 1

  if (!newProduct.value.category_id) {
    notifyError('Deve escolher a categoria')
    return
  }

  try {
    await productApi.create({
      ...newProduct.value,
      company_id: companyId,
      is_available: true
    })
    showAddModal.value = false
    newProduct.value = { name: '', description: '', price: 0, category_id: null }
    loadProducts()
    notifySuccess('Produto criado com sucesso!')
  } catch (error: any) {
    notifyError(formatApiError(error))
  }
}

// Image Management
const triggerFileInput = () => {
  fileInput.value?.click()
}

const handleFileUpload = async (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  
  if (!file || !selectedProduct.value) return
  
  uploading.value = true
  uploadProgress.value = 0
  
  try {
    const isMain = !selectedProduct.value.image_url
    const res = await productApi.uploadImage(selectedProduct.value.id, file, isMain)
    
    // Update local product
    const index = products.value.findIndex(p => p.id === selectedProduct.value?.id)
    if (index !== -1) {
      products.value[index] = res.data
      selectedProduct.value = res.data
    }
    
    notifySuccess('Imagem enviada com sucesso!')
  } catch (error: any) {
    notifyError(error.response?.data?.detail || 'Erro ao enviar imagem')
  } finally {
    uploading.value = false
    uploadProgress.value = 0
    // Reset file input
    if (fileInput.value) {
      fileInput.value.value = ''
    }
  }
}

const deleteImage = async (imageUrl: string) => {
  if (!selectedProduct.value) return
  
  if (!await confirmDialog({ title: 'Confirmar exclusão', message: 'Tem certeza que deseja excluir esta imagem?', confirmLabel: 'Excluir', danger: true })) return
  
  try {
    const res = await productApi.deleteImage(selectedProduct.value.id, imageUrl)
    
    // Update local product
    const index = products.value.findIndex(p => p.id === selectedProduct.value?.id)
    if (index !== -1) {
      products.value[index] = res.data
      selectedProduct.value = res.data
    }
    
    notifySuccess('Imagem excluída com sucesso!')
  } catch (error: any) {
    notifyError(error.response?.data?.detail || 'Erro ao excluir imagem')
  }
}

const setMainImage = async (imageUrl: string) => {
  if (!selectedProduct.value) return
  
  try {
    const res = await productApi.setMainImage(selectedProduct.value.id, imageUrl)
    
    // Update local product
    const index = products.value.findIndex(p => p.id === selectedProduct.value?.id)
    if (index !== -1) {
      products.value[index] = res.data
      selectedProduct.value = res.data
    }
    
    notifySuccess('Imagem principal atualizada!')
  } catch (error: any) {
    notifyError(error.response?.data?.detail || 'Erro ao atualizar imagem principal')
  }
}

onMounted(() => {
  loadProducts()
  loadCategories()
})
</script>
